import { readVisibilityStateFromProps } from "../utils/visibilityState";
import { annoDimForRequest } from "../utils/annoDimForRequest";

/**
 * Convert SimpleTable data format to customRanges array for backend API
 * SimpleTable format: { data: [{ cellData: [{value: min}, {value: max}] }, ...] }
 * Backend format: [{min: number, max: number}, ...]
 */
const convertSimpleTableToCustomRanges = (tableData) => {
  if (!tableData?.data || !Array.isArray(tableData.data)) {
    return null;
  }

  const ranges = tableData.data
    .map(row => {
      if (!row.cellData || row.cellData.length < 2) return null;
      const minVal = parseFloat(row.cellData[0]?.value);
      const maxVal = parseFloat(row.cellData[1]?.value);
      // Only include rows where both min and max are valid numbers
      if (isNaN(minVal) || isNaN(maxVal)) return null;
      return { min: minVal, max: maxVal };
    })
    .filter(Boolean);

  return ranges.length > 0 ? ranges : null;
};

export const processRawConfigFn = (_widgetConfig) => {
  // console.log({_widgetConfig})
  //new processing
  let contentType;
  const dimensions = [];
  let presetActiveFilters = [];
  const RAW_dims = [
    _widgetConfig.props?.dimension1,
    _widgetConfig.props?.dimension2,
    _widgetConfig.props?.dimension3,
    _widgetConfig.props?.dimension4,
  ].filter(Boolean);

  // Get custom ranges for each dimension (if NumberInput)
  const customRangesForDims = [
    convertSimpleTableToCustomRanges(_widgetConfig.props?.dimension1CustomRanges),
    convertSimpleTableToCustomRanges(_widgetConfig.props?.dimension2CustomRanges),
    convertSimpleTableToCustomRanges(_widgetConfig.props?.dimension3CustomRanges),
    convertSimpleTableToCustomRanges(_widgetConfig.props?.dimension4CustomRanges),
  ];

  // Get numberOfBuckets for each dimension (if NumberInput, for auto-bucketing)
  const numberOfBucketsForDims = [
    _widgetConfig.props?.dimension1NumberOfBuckets,
    _widgetConfig.props?.dimension2NumberOfBuckets,
    _widgetConfig.props?.dimension3NumberOfBuckets,
    _widgetConfig.props?.dimension4NumberOfBuckets,
  ];

  // Get bucketSize for each dimension (if NumberInput, for fixed-width bucketing)
  const bucketSizeForDims = [
    _widgetConfig.props?.dimension1BucketSize,
    _widgetConfig.props?.dimension2BucketSize,
    _widgetConfig.props?.dimension3BucketSize,
    _widgetConfig.props?.dimension4BucketSize,
  ];

  // "occurrence" | "histogram" per dimension (see NumberFieldConfig). Widgets saved
  // before this key existed don't have it, so it's derived below the same way
  // NumberFieldConfig derives it: no bucket config means occurrence.
  const representationForDims = [
    _widgetConfig.props?.dimension1NumberFieldRepresentation,
    _widgetConfig.props?.dimension2NumberFieldRepresentation,
    _widgetConfig.props?.dimension3NumberFieldRepresentation,
    _widgetConfig.props?.dimension4NumberFieldRepresentation,
  ];

  if(RAW_dims.length === 0) return _widgetConfig
  /**
   * this applies a title to the legend.
   * but first we need to figure if this _widgetConfig will generate
   * a breakdown or not.
   * 
   * so if only 1 dimension exists and it is of comp == 'GroupQuestionsInputSingle'
   * then category name is, well the name of the main question.. i.e .display
   * else if 2+ dimensions exist then... the second dimension is the rollup one. we simply can use its .display.
   * .display is what shows up in the dropdown and that should be good as the legend title
   */

  if (RAW_dims.length !== 0) {
    contentType = RAW_dims.some((d) => d.isAnno)
      ? "annotations"
      : RAW_dims[0].contentType;

    RAW_dims.forEach((dim, dimIndex) => {
      const customRanges = customRangesForDims[dimIndex];
      const numberOfBuckets = numberOfBucketsForDims[dimIndex];
      const bucketSize = bucketSizeForDims[dimIndex];

      const representation =
        representationForDims[dimIndex] ||
        (customRanges || numberOfBuckets || bucketSize ? "histogram" : "occurrence");

      // Explicit opt-out of the backend's default range bucketing: group by exact
      // value instead. Expressions are always continuous, so they never opt out.
      const countPerNumber =
        dim.comp === "NumberInput" &&
        !dim.isExpression &&
        representation === "occurrence";

      // Helper to spread the number-field config props
      const bucketProps = {
        ...(customRanges ? { customRanges } : {}),
        ...(numberOfBuckets ? { numberOfBuckets } : {}),
        ...(bucketSize ? { bucketSize } : {}),
        ...(countPerNumber ? { countPerNumber: true } : {}),
      };

      if (!dim.rollupType) {
        //means its a normal tag or option select field option

        if (contentType === "annotations") {
          if (dim.dimensionType === "topicClusteringNode" || dim.valuePath?.startsWith("annotations.")) {
            // annotations.* paths are typically tags, not numbers - customRanges not applicable.
            // Topics dimensions have no valuePath at all (see annoDimForRequest).
            dimensions.push(annoDimForRequest(dim));
          } else {
            // contentType has to be explicitly defined for this field
            dimensions.push({
              contentType: dim.contentType,
              valuePath: dim.valuePath,
              ...bucketProps,
            });
          }
        } else {
          // For non-annotation content types
          if (dim.isExpression) {
            // Handle arithmetic expression dimensions
            dimensions.push({
              valuePath: dim.valuePath,
              isExpression: true,
              ...bucketProps,
            });
          } else if (customRanges || numberOfBuckets || bucketSize || countPerNumber) {
            // If any number-field config exists, send as object with valuePath
            dimensions.push({
              valuePath: dim.valuePath,
              ...bucketProps,
            });
          } else {
            // Nothing to configure, send as simple string
            dimensions.push(dim.valuePath);
          }
        }
      } else if (dim.rollupType === "tagType") {
        dimensions.push({
          rollupType: dim.rollupType,
          path: dim.rollupPath,
          ...(dim.isAnno ? { isAnno: dim.isAnno } : {}),
          // Restrict the group-by buckets to a specific set of tag/sub-theme ids
          // (folder-level annotation select). Backend applies a post-rollup $match.
          ...(dim.restrictBucketValues ? { restrictBucketValues: dim.restrictBucketValues } : {}),
          ...bucketProps,
        });

      } else if (dim.rollupType === "valuePathType") {
        dimensions.push({
          rollupType: dim.rollupType,
          path: [dim.tagTypeCollectionToRollup, dim.blockValuePath],
          ...(dim.isAnno ? { isAnno: dim.isAnno } : {}),
          ...bucketProps,
        });
      }
      //in general add the presetActiveFilters if they exist
      if(dim.presetActiveFilters){
        presetActiveFilters = [
          ...presetActiveFilters,
          ...dim.presetActiveFilters
        ]
      }
    });
  }

  // console.log({contentTypeToReturn: contentType})

  const toReturn = {
    ..._widgetConfig,
    props: {
      ..._widgetConfig.props,
      contentType,
      dimensions,
      presetActiveFilters,
      // Combine-segments (group smallest slices into "Other") for single-layer pies.
      // pieChartType goes along so the render can tell a multi-layer pie that only
      // has layer 1 configured yet from a genuine single-layer one.
      pieChartType: _widgetConfig.props?.pieChartType,
      // Default ON is reports-only — DataPivot/Overview skip processRawConfigFn
      // and must stay OFF unless opted in.
      combineSegments: _widgetConfig.props?.combineSegments ?? true,
      numberOfSegments: _widgetConfig.props?.numberOfSegments,
      // Pass through hidden datapoints for show/hide feature
      // Normalised visibility state per layer ({mode, dynamicPreset, n,
      // manualHidden}). The chart resolves what to hide from this against the
      // CURRENT data, so a Top-N layer can't go stale after a filter change.
      dim1Visibility: readVisibilityStateFromProps(_widgetConfig.props, 1),
      dim2Visibility: readVisibilityStateFromProps(_widgetConfig.props, 2),
      dim3Visibility: readVisibilityStateFromProps(_widgetConfig.props, 3),
      dim4Visibility: readVisibilityStateFromProps(_widgetConfig.props, 4),
    },
  };
  return toReturn;
};

